import { Component, OnChanges } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Input, Output, EventEmitter } from '@angular/core';

@Component({
    selector: 'upp-color',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './upp-color.html',
    styleUrls: [
        './upp-color.scss'
    ]
})
export class UppColorComponent implements OnChanges {
    @Input() label = '';
    @Input() value: string | null = '#000000';
    @Input() dataid = 'upp-color-input';
    @Input() placeholder = 'RRGGBB';

    @Output() Changed = new EventEmitter<string>();

    private _pickerstate = { h: 0, s: 0, v: 0 };
    private _showpicker = false;
    private _hex = '000000';

    get ShowPicker(): boolean {
        return this._showpicker;
    }

    get HexValue(): string {
        return this._hex;
    }

    get PreviewColor(): string {
        return `#${this._hex.padEnd(6, '0').slice(0, 6)}`;
    }

    get PickerSurfaceColor(): string {
        const _rgb = this._hsvToRgb(this._pickerstate.h, 1, 1);
        return this._rgbToHex(_rgb.r, _rgb.g, _rgb.b);
    }

    get PickerCursorLeft(): number {
        return this._pickerstate.s * 100;
    }

    get PickerCursorTop(): number {
        return (1 - this._pickerstate.v) * 100;
    }

    get PickerHuePosition(): number {
        return this._pickerstate.h;
    }

    get RgbText(): string {
        const _rgb = this._hexToRgb(this._hex);
        if (!_rgb){
            return '';
        }
        return `${_rgb.r}, ${_rgb.g}, ${_rgb.b}`;
    }

    constructor(private change: ChangeDetectorRef) {
    }

    ngOnChanges() {
        const _next = this._normalizeHex(this.value);
        this._setHex(_next, false);
    }

    OnTogglePicker(event: Event | null = null): void {
        if (event){
            event.preventDefault();
            event.stopPropagation();
        }

        this._showpicker = !this._showpicker;
        if (this._showpicker){
            this._syncPickerStateFromHex(this._hex);
        }
        this.change.markForCheck();
    }

    OnHexChanged(value: string | null): void {
        const _next = this._normalizeHex(value);
        this._setHex(_next, true);
    }

    OnPickerSurfacePointerDown(event: PointerEvent): void {
        if (event.currentTarget && 'setPointerCapture' in (event.currentTarget as HTMLElement)){
            (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
        }
        this._updatePickerSurface(event);
    }

    OnPickerSurfacePointerMove(event: PointerEvent): void {
        if ((event.buttons & 1) !== 1){
            return;
        }
        this._updatePickerSurface(event);
    }

    OnPickerHueChanged(value: string | null): void {
        const _hue = Number(value);
        if (isNaN(_hue)){
            return;
        }

        this._pickerstate.h = this._clamp(_hue, 0, 360);
        if (this._pickerstate.s === 0 && this._pickerstate.v > 0){
            // When current color is white/gray, hue-only changes are visually inert.
            // Promote saturation so hue interaction becomes immediately visible.
            this._pickerstate.s = 1;
        }
        this._applyPickerState();
    }

    private _setHex(value: string, emit: boolean): void {
        const _next = this._normalizeHex(value);
        if (this._hex === _next){
            if (emit){
                this.Changed.emit(_next);
            }
            return;
        }

        this._hex = _next;
        if (_next.length === 6){
            this._syncPickerStateFromHex(_next);
        }

        if (emit){
            this.Changed.emit(_next);
        }

        this.change.markForCheck();
    }

    private _updatePickerSurface(event: PointerEvent): void {
        const _target = event.currentTarget as HTMLElement | null;
        if (!_target){
            return;
        }

        const _rect = _target.getBoundingClientRect();
        if (_rect.width <= 0 || _rect.height <= 0){
            return;
        }

        const _saturation = this._clamp((event.clientX - _rect.left) / _rect.width, 0, 1);
        const _value = this._clamp(1 - ((event.clientY - _rect.top) / _rect.height), 0, 1);

        this._pickerstate.s = _saturation;
        this._pickerstate.v = _value;
        this._applyPickerState();
    }

    private _applyPickerState(): void {
        const _rgb = this._hsvToRgb(this._pickerstate.h, this._pickerstate.s, this._pickerstate.v);
        const _hex = this._rgbToHex(_rgb.r, _rgb.g, _rgb.b).replace('#', '');
        this._setHex(_hex, true);
    }

    private _syncPickerStateFromHex(hex: string): void {
        const _rgb = this._hexToRgb(hex);
        if (!_rgb){
            return;
        }

        this._pickerstate = this._rgbToHsv(_rgb.r, _rgb.g, _rgb.b);
    }

    private _normalizeHex(value: string | null): string {
        return String(value || '')
            .replace(/#/g, '')
            .replace(/[^a-fA-F0-9]/g, '')
            .slice(0, 6)
            .toLowerCase();
    }

    private _hexToRgb(hex: string | null): { r: number; g: number; b: number } | null {
        const _value = this._normalizeHex(hex);
        if (_value.length !== 6){
            return null;
        }

        return {
            r: parseInt(_value.slice(0, 2), 16),
            g: parseInt(_value.slice(2, 4), 16),
            b: parseInt(_value.slice(4, 6), 16),
        };
    }

    private _rgbToHex(r: number, g: number, b: number): string {
        const _toHex = (value: number) => this._clamp(Math.round(value), 0, 255).toString(16).padStart(2, '0');
        return `#${_toHex(r)}${_toHex(g)}${_toHex(b)}`;
    }

    private _hsvToRgb(h: number, s: number, v: number): { r: number; g: number; b: number } {
        const _h = ((h % 360) + 360) % 360;
        const _s = this._clamp(s, 0, 1);
        const _v = this._clamp(v, 0, 1);
        const _c = _v * _s;
        const _x = _c * (1 - Math.abs(((_h / 60) % 2) - 1));
        const _m = _v - _c;

        let _r = 0;
        let _g = 0;
        let _b = 0;

        if (_h < 60){
            _r = _c; _g = _x; _b = 0;
        }
        else if (_h < 120){
            _r = _x; _g = _c; _b = 0;
        }
        else if (_h < 180){
            _r = 0; _g = _c; _b = _x;
        }
        else if (_h < 240){
            _r = 0; _g = _x; _b = _c;
        }
        else if (_h < 300){
            _r = _x; _g = 0; _b = _c;
        }
        else {
            _r = _c; _g = 0; _b = _x;
        }

        return {
            r: (_r + _m) * 255,
            g: (_g + _m) * 255,
            b: (_b + _m) * 255,
        };
    }

    private _rgbToHsv(r: number, g: number, b: number): { h: number; s: number; v: number } {
        const _r = this._clamp(r, 0, 255) / 255;
        const _g = this._clamp(g, 0, 255) / 255;
        const _b = this._clamp(b, 0, 255) / 255;
        const _max = Math.max(_r, _g, _b);
        const _min = Math.min(_r, _g, _b);
        const _delta = _max - _min;
        let _h = 0;

        if (_delta !== 0){
            if (_max === _r){
                _h = 60 * (((_g - _b) / _delta) % 6);
            }
            else if (_max === _g){
                _h = 60 * (((_b - _r) / _delta) + 2);
            }
            else {
                _h = 60 * (((_r - _g) / _delta) + 4);
            }
        }

        if (_h < 0){
            _h += 360;
        }

        return {
            h: _h,
            s: (_max === 0) ? 0 : _delta / _max,
            v: _max,
        };
    }

    private _clamp(value: number, min: number, max: number): number {
        return Math.min(max, Math.max(min, value));
    }
}
